import { useCallback, useRef, useState } from 'react';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fireCmdbErrorAnalytics } from '@atlassian/jira-servicedesk-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { performCmdbGetRequest } from '@atlassian/jira-servicedesk-cmdb-client/src/fetch.tsx';
import { getObjectDetailsUrl } from '@atlassian/jira-servicedesk-insight-object-services/src/common/utils.tsx';
import type { InsightObjectDetailsResponse } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-details.tsx';
import type {
	WorkspaceId,
	CmdbObjectId,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';

export const useFetchObjectDetails = (
	workspaceId: WorkspaceId,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
) => {
	const requestSignal = useRef<AbortController | undefined>();
	const [loading, setLoading] = useState<boolean>(false);
	const [data, setData] = useState<InsightObjectDetailsResponse>();
	const [error, setError] = useState<Error | undefined>();

	const request = useCallback(
		async (objectId: CmdbObjectId) => {
			setLoading(true);
			setError(undefined);
			if (requestSignal.current) {
				requestSignal.current.abort();
			}

			try {
				requestSignal.current = new AbortController();
				const response: InsightObjectDetailsResponse = await performCmdbGetRequest(
					getObjectDetailsUrl(workspaceId, objectId),
					{
						signal: requestSignal.current.signal,
					},
				);

				fireTrackAnalytics(createAnalyticsEvent({}), 'fetchObjectDetails succeeded');
				setData(response);
				setLoading(false);

				return response;
			} catch (err: unknown) {
				const typesafeError = err instanceof Error ? err : new Error('Unknown error');

				if (typesafeError && typesafeError.name === 'AbortError') {
					return undefined;
				}
				fireCmdbErrorAnalytics({
					error: typesafeError,
					meta: {
						id: 'fetchObjectDetails',
						packageName: 'jiraServicedeskInsightObjectView',
					},
				});

				setLoading(false);
				setError(typesafeError);

				return undefined;
			}
		},
		[createAnalyticsEvent, workspaceId],
	);

	return {
		loading,
		error,
		data,
		fetchObjectDetails: request,
	};
};
